import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Search, Email, Phone, CalendarToday, Close, FiberManualRecord, History } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../services/apiService';
import { PackPaymentTypeChip } from '../components/PackBookingTooltip';
import { colors } from '../theme/theme';

interface Customer {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  profilePicture?: string;
  bookingCount: number;
  lastBookingDate: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  BOOKED:     { label: 'Booked',     color: '#1565C0' },
  CHECKED_IN: { label: 'Checked In', color: '#2E7D32' },
  CANCELLED:  { label: 'Cancelled',  color: '#C62828' },
  NO_SHOW:    { label: 'No Show',    color: '#E64A19' },
  COMPLETED:  { label: 'Completed',  color: '#2E7D32' },
};

const BOOKING_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  workout:  { label: 'Workout',  color: '#1565C0' },
  event:    { label: 'Event',    color: '#6A1B9A' },
  wellness: { label: 'Wellness', color: '#00838F' },
  trial:    { label: 'Trial',    color: '#E65100' },
};

const PAYMENT_MODE_CONFIG: Record<string, { label: string; color: string }> = {
  online:       { label: 'Online',       color: colors.success },
  offline:      { label: 'Offline',      color: colors.warning },
  pay_at_venue: { label: 'Pay at Venue', color: '#0277BD' },
  free:         { label: 'Free',         color: '#78909C' },
  cash:         { label: 'Cash',         color: '#F57F17' },
  subscription: { label: 'Pack',         color: '#6A1B9A' },
};

function formatIST(dateStr: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function StatusChip({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#78909C' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: `${cfg.color} !important` }} />}
      sx={{
        backgroundColor: `${cfg.color}15`,
        color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
}

function TypeChip({ type, config }: { type: string; config: Record<string, { label: string; color: string }> }) {
  const cfg = config[type] || { label: type, color: '#78909C' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: `${cfg.color}15`,
        color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        fontWeight: 500,
        fontSize: '0.7rem',
      }}
    />
  );
}

// ─── Customer History Dialog ──────────────────────────────────────────────────

function CustomerHistoryDialog({ customer, open, onClose }: {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
}) {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const rowsPerPage = 20;

  const fetchBookings = useCallback(async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const params: any = { page: page + 1, pageSize: rowsPerPage, sort: '-1', userId: customer._id };
      if (statusFilter) params.status = statusFilter;
      const res = await apiService.getStudioScopedBookings(params);
      setBookings(res.bookings || []);
      setTotal(res.count || 0);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [customer, page, statusFilter]);

  useEffect(() => {
    if (open && customer) {
      setPage(0);
      setStatusFilter('');
    }
  }, [open, customer]);

  useEffect(() => {
    if (open && customer) fetchBookings();
  }, [fetchBookings, open, customer]);

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar src={customer.profilePicture} sx={{ bgcolor: colors.primary, width: 36, height: 36, fontSize: 14 }}>
            {customer.name?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{customer.name}</Typography>
            <Typography variant="caption" color="text.secondary">{customer.phone || customer.email || '—'}</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {total} booking{total !== 1 ? 's' : ''} total
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="BOOKED">Booked</MenuItem>
              <MenuItem value="CHECKED_IN">Checked In</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="NO_SHOW">No Show</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : bookings.length === 0 ? (
          <Alert severity="info">No bookings found.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Class / Event</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Class Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Booked On</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((b) => {
                  const className = b.session?.className || b.event?.eventName || '—';
                  const activityDate = b.session?.date || b.event?.date || b.wellnessSlotStart;
                  const paymentMode = b.transaction?.modeOfPayment || b.modeOfPayment || null;
                  const isPackBooking = paymentMode === 'subscription';
                  const purchasedPack = b.transaction?.purchasedPack || null;
                  const amount = (b.transaction?.basePrice ?? 0) + (b.transaction?.taxes ?? 0);
                  const showTimestamp = b.bookingStatus === 'CHECKED_IN' || b.bookingStatus === 'NO_SHOW';
                  const timestampLabel = b.bookingStatus === 'CHECKED_IN' ? 'Checked in' : 'No show';

                  return (
                    <TableRow
                      key={b._id}
                      hover
                      sx={{ opacity: b.bookingStatus === 'CANCELLED' ? 0.6 : 1 }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                          {className}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {activityDate ? formatDate(activityDate) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <StatusChip status={b.bookingStatus} />
                          {showTimestamp && b.updatedAt && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3 }}>
                              {timestampLabel}: {formatIST(b.updatedAt)}
                            </Typography>
                          )}
                          {b.cancellationReason && (
                            <Tooltip title={b.cancellationReason}>
                              <Typography variant="caption" color="error.main" display="block" noWrap sx={{ maxWidth: 160, mt: 0.3 }}>
                                {b.cancellationReason}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {b.bookingType ? <TypeChip type={b.bookingType} config={BOOKING_TYPE_CONFIG} /> : '—'}
                      </TableCell>
                      <TableCell>
                        {isPackBooking ? (
                          <PackPaymentTypeChip pack={purchasedPack} config={PAYMENT_MODE_CONFIG} />
                        ) : paymentMode ? (
                          <Box>
                            <TypeChip type={paymentMode} config={PAYMENT_MODE_CONFIG} />
                            {amount > 0 && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.3 }}>
                                ₹{amount.toLocaleString('en-IN')}
                              </Typography>
                            )}
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap>
                          {formatIST(b.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[20]}
            />
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const StudioCustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [historyCustomer, setHistoryCustomer] = useState<Customer | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getStudioMembers({
        page: page + 1,
        limit: pageSize,
        ...(search ? { search } : {}),
      });
      setCustomers(res?.data || []);
      setTotal(res?.total || 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    const timer = setTimeout(fetchCustomers, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchCustomers, search]);

  const handleRowClick = (params: any) => {
    setHistoryCustomer(params.row as Customer);
    setHistoryOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Customer',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Avatar
            src={params.row.profilePicture}
            sx={{ width: 36, height: 36, bgcolor: colors.primary, fontSize: 14 }}
          >
            {params.value?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Typography variant="body2" fontWeight={500}>
            {params.value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="body2">{params.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="body2">{params.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'lastBookingDate',
      headerName: 'Last Booking',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value
              ? new Date(params.value).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: '_actions',
      headerName: '',
      width: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%', color: 'primary.main' }}>
          <History sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
            View History
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Customers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click any customer to view their booking history
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {total} total customer{total !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, email or phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <DataGrid
          rows={customers}
          columns={columns}
          getRowId={(row) => row._id}
          rowCount={total}
          loading={loading}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          rowHeight={60}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.background,
              borderBottom: `1px solid ${colors.divider}`,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0,0,0,0.03)',
            },
          }}
        />
      </Card>

      <CustomerHistoryDialog
        customer={historyCustomer}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </Box>
  );
};

export default StudioCustomersPage;
